import * as React from 'react';
import { useEffect, useState } from "react";
import {
  getAdminAssessmentQuestions,
  createAdminAssessmentQuestion,
  getAdminLearningObjectives,
  getAdminReusableObjects,
} from "../../../../../api/sdk.gen";
import { AssessmentQuestion, LearningObjective, ReusableObject } from "../../../../../api/types.gen";
import GenModal from '../../../../Gen/Modal/component';
import { truncateWithTooltip, getMultiselectOptions, formatLabel } from '../../../../../helpers/utils';
import MySwal from "../../../../../ui/MySwal";
import Select from 'react-select';
import AsyncSelect from 'react-select/async';

interface AdminAssessmentQuestionsNewProps {
  coursesOptions: string[];
  casesOptions: string[];
  questionCreatedCallback: () => void;
}

const AdminAssessmentQuestionsNew: React.FC<AdminAssessmentQuestionsNewProps> = ({ coursesOptions, casesOptions, questionCreatedCallback }) => {
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState('');
  const [selectedCase, setSelectedCase] = useState(null);
  const [learningObjectives, setLearningObjectives] = useState([]);
  const [reusableObjects, setReusableObjects] = useState([]);
  const [selectedLearningObjective, setSelectedLearningObjective] = useState(null);
  const [selectedReusableObject, setSelectedReusableObject] = useState(null);

  useEffect(() => {
    const fetchLearningObjectives = async () => {
      if (selectedCase) {
        const options = await loadLearningObjectives('');
        setLearningObjectives(options);
      } else {
        setLearningObjectives([]);
      }
    };

    fetchLearningObjectives();
  }, [selectedCase]);

  useEffect(() => {
    const fetchReusableObjects = async () => {
      if (selectedLearningObjective) {
        const options = await loadReusableObjects('');
        setReusableObjects(options);
      } else {
        setReusableObjects([]);
      }
    };

    fetchReusableObjects();
  }, [selectedLearningObjective]);

  // Handler for "Close" button
  const handleClose = () => {
    setModalOpen(false);
    questionCreatedCallback();
  };

  // Handler for "Create Question" button
  const handleCreateQuestion = () => {
    setModalOpen(true);
  };

  // Handler for "Save Question" button
  const handleSaveQuestion = async () => {
    const result = await createAdminAssessmentQuestion({
      body: {
        document_id: selectedCase,
        learning_objective_id: selectedLearningObjective,
        reusable_object_id: selectedReusableObject
      }
    });

    if (result.error) {
      console.error("Error saving question set:", result.error);
      MySwal.fire("Error!", "Failed to save question set. - " + (result as { error: { error: string } }).error.error, "error");
    } else {
      setSelectedCourse('');
      setSelectedCase(null);
      setSelectedLearningObjective(null);
      setSelectedReusableObject(null);
      MySwal.fire("Created!", "Question set have been created.", "success");
    }
  };

  // Fetch learning objectives based on search term
  const loadLearningObjectives = async (inputValue: string) => {
    try {
      const response = await getAdminLearningObjectives({
        query: { filters: { query: inputValue, document_id: selectedCase, new_question: true } },
      });
      return response.data.data.map((item) => ({
        label: `${item.id} - ${item.uid} - ${item.name}`,
        value: item.id,
      }));
    } catch (error) {
      console.error('Failed to fetch learning objectives:', error);
      return [];
    }
  };

  // Fetch reusable objects based on search term
  const loadReusableObjects = async (inputValue: string) => {
    try {
      const response = await getAdminReusableObjects({
        query: { filters: { query: inputValue, document_id: selectedCase, learning_objective: selectedLearningObjective, new_question: true } },
      });
      return response.data.data.map((item: ReusableObject) => ({
        label: `${item.id} - ${item.uid} - ${item.name}`,
        value: item.id,
      }));
    } catch (error) {
      console.error('Failed to fetch reusable objects:', error);
      return [];
    }
  };

  // Validate if Save button should be enabled
  const isSaveDisabled = !selectedCourse || !selectedCase || !selectedLearningObjective || !selectedReusableObject;

  console.log('learningObjectives:', learningObjectives);
  console.log('selectedLearningObjective:', selectedLearningObjective);

  return (
    <>
      <button onClick={handleCreateQuestion}>
        Create Question
      </button>

      <GenModal open={modalOpen}>
        <div className="gen-modal-panel">
          <h5>Create Question</h5>

          <div style={{ paddingTop: 20, paddingBottom: 20 }}>Newly created questions will appear in the Question Bank and can be found by filtering by ‘new’ in the Stem field</div>

          <div style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "normal",
            columnGap: 20,
            rowGap: 20,
            flexDirection: 'column',
          }}>
            <Select
              isClearable
              isSearchable
              options={getMultiselectOptions(coursesOptions)}
              value={getMultiselectOptions(coursesOptions).find(option => selectedCourse === option.value.toString())}
              onChange={selected => { setSelectedCourse(selected?.value.toString() || ''); setSelectedCase('') }}
              placeholder="Select Course"
            />

            <Select
              isClearable
              isSearchable
              isDisabled={selectedCourse === ''}
              options={getMultiselectOptions(casesOptions, true).filter(option => option.label.toString().indexOf(formatLabel(selectedCourse)) >= 0)}
              value={getMultiselectOptions(casesOptions).find(option => selectedCase === option.value.toString())}
              onChange={selected => setSelectedCase(selected?.value.toString() || '')}
              placeholder="Select Case"
            />

            <Select
              isClearable
              isSearchable
              isDisabled={selectedCase === ''}
              options={learningObjectives}
              value={learningObjectives.find(option => option.value === parseInt(selectedLearningObjective)) || null}
              onChange={selected => setSelectedLearningObjective(selected?.value.toString())}
              placeholder="Select LO"
            />

            <Select
              isClearable
              isSearchable
              isDisabled={!selectedLearningObjective}
              options={reusableObjects}
              value={reusableObjects.find(option => option.value === parseInt(selectedReusableObject)) || null}
              onChange={selected => setSelectedReusableObject(selected?.value.toString())}
              placeholder="Select TP"
            />
          </div>

          <div className="controls" style={{
            display: 'flex',
            justifyContent: 'flex-end',
            marginTop: 30,
            columnGap: 20,
          }}>
            <button style={{margin: 0}} onClick={handleSaveQuestion} disabled={isSaveDisabled}>Save Question</button>
            <button onClick={handleClose}>Finish and close</button>
          </div>
        </div>
      </GenModal>
    </>
  );
};

export default AdminAssessmentQuestionsNew;
